import { memo, useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Info } from 'lucide-react'
import { announcementsApi, type Airtime } from '../../lib/infoserviceApi.js'
import { addDays, shortDayLabel } from '../../lib/announcementGrid.js'
import { formatAirtime } from '../../lib/memberQuota.js'
import { Spinner } from '../common/Spinner.js'
import './InfoserviceMembers.css'

interface AirtimePanelProps {
  channelId: string | null
  today: string
  /** Mode de la chaîne : le suivi n'existe qu'en diaporama. */
  slideshow: boolean | null
}

const CLOCK = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris' })

/**
 * Onglet Suivi : temps d'antenne de l'infoservice sur une journée — chaque
 * créneau, puis chaque annonce. Mode diaporama seulement : en fichier vidéo,
 * TrafficPlay ne sait pas ce que contient le MP4 (décision de l'exploitant).
 */
export const AirtimePanel = memo(function AirtimePanel({ channelId, today, slideshow }: AirtimePanelProps) {
  const [day, setDay] = useState(today)
  const [data, setData] = useState<Airtime | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!channelId) return
    setLoading(true)
    setError('')
    try {
      setData(await announcementsApi.airtime(channelId, day))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Chargement impossible')
    } finally {
      setLoading(false)
    }
  }, [channelId, day])

  useEffect(() => { void load() }, [load])

  const announcementsTotal = data?.announcements.reduce((total, entry) => total + entry.airings, 0) ?? 0

  return (
    <section id="infoservice-tab-panel" role="tabpanel" aria-labelledby="infoservice-tab-airtime" className="announcements-workspace airtime-panel">
      <div className="announcements-summary">
        <div className="announcements-summary__title">
          <h2 className="announcements-summary__heading">Suivi d’antenne</h2>
          <p className="announcements-summary__note">Durée réelle des créneaux Infoservice et temps de chaque annonce, en mode diaporama.</p>
        </div>
        <div className="announcements-summary__metric">
          <span className="announcements-summary__label">Infoservice</span>
          <span className="announcements-summary__value">{data ? formatAirtime(data.totalMs) : '—'}</span>
          <span className="announcements-summary__label">{data ? `${data.plages.length} créneau${data.plages.length > 1 ? 'x' : ''}` : ' '}</span>
        </div>
        <div className="announcements-summary__metric">
          <span className="announcements-summary__label">Annonces</span>
          <span className="announcements-summary__value">{data ? data.announcements.length : '—'}</span>
          <span className="announcements-summary__label">{announcementsTotal} passage{announcementsTotal > 1 ? 's' : ''}</span>
        </div>
        <div className="announcements-summary__tools airtime-panel__day">
          <button type="button" className="tp-icon-button" onClick={() => setDay(current => addDays(current, -1))} aria-label="Jour précédent"><ChevronLeft size={14} aria-hidden="true" /></button>
          <input className="tp-input" type="date" aria-label="Jour suivi" value={day} max={today} onChange={event => { if (event.target.value) setDay(event.target.value) }} />
          <button type="button" className="tp-icon-button" onClick={() => setDay(current => addDays(current, 1))} disabled={day >= today} aria-label="Jour suivant"><ChevronRight size={14} aria-hidden="true" /></button>
          {day !== today && <button type="button" className="tp-button" onClick={() => setDay(today)}>Aujourd’hui</button>}
        </div>
      </div>

      {slideshow === false && (
        <div className="announcements-attention airtime-panel__note" role="note">
          <Info size={14} aria-hidden="true" />
          <span>La chaîne diffuse le fichier MP4 du jour : ses créneaux ne sont pas suivis. Le suivi commence avec le mode diaporama.</span>
        </div>
      )}
      {error && <div className="tp-feedback tp-feedback--error announcements-feedback" role="alert">{error}</div>}

      {loading && !data ? (
        <div className="tp-empty-state announcements-state" aria-live="polite"><Spinner /> Chargement…</div>
      ) : data && data.plages.length === 0 ? (
        <div className="tp-empty-state announcements-state">
          {`Aucun créneau en diaporama le ${shortDayLabel(day)}.`}
          {data.untrackedPlages > 0 && ` ${data.untrackedPlages} créneau${data.untrackedPlages > 1 ? 'x' : ''} en fichier vidéo, non suivi${data.untrackedPlages > 1 ? 's' : ''}.`}
        </div>
      ) : data && (
        <div className="airtime-panel__columns">
          <div className="announcements-table-wrap airtime-panel__card">
            <h3 className="airtime-panel__heading">Créneaux</h3>
            <table className="airtime-table">
              <thead>
                <tr>
                  <th scope="col">Créneau</th>
                  <th scope="col" className="airtime-table__number">Durée</th>
                  <th scope="col" className="airtime-table__number">Annonces</th>
                </tr>
              </thead>
              <tbody>
                {data.plages.map(plage => (
                  <tr key={plage.firstItemId}>
                    <th scope="row" className="airtime-table__time">
                      {CLOCK.format(new Date(plage.start))} → {plage.ongoing ? 'en cours' : CLOCK.format(new Date(plage.end))}
                    </th>
                    <td className="airtime-table__number">
                      {formatAirtime(plage.durationMs)}
                      {plage.estimated && <span className="airtime-table__flag" title="Fin réelle inconnue : fin prévue par la grille">estimée</span>}
                    </td>
                    <td className="airtime-table__number">{plage.airings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.untrackedPlages > 0 && (
              <p className="airtime-panel__foot">{data.untrackedPlages} autre{data.untrackedPlages > 1 ? 's' : ''} créneau{data.untrackedPlages > 1 ? 'x' : ''} en fichier vidéo, non suivi{data.untrackedPlages > 1 ? 's' : ''}.</p>
            )}
          </div>

          <div className="announcements-table-wrap airtime-panel__card">
            <h3 className="airtime-panel__heading">Annonces</h3>
            <div className="airtime-panel__scroll">
              <table className="airtime-table">
                <thead>
                  <tr>
                    <th scope="col">Annonce</th>
                    <th scope="col" className="airtime-table__number">Passages</th>
                    <th scope="col" className="airtime-table__number">Temps</th>
                  </tr>
                </thead>
                <tbody>
                  {data.announcements.map(entry => (
                    <tr key={entry.announcementId}>
                      <th scope="row" className="airtime-table__name">
                        <span className="airtime-table__organization">{entry.organizationLabel}</span>
                        {entry.eventLabel && <span className="airtime-table__event">{entry.eventLabel}</span>}
                      </th>
                      <td className="airtime-table__number">{entry.airings}</td>
                      <td className="airtime-table__number">{formatAirtime(entry.totalMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  )
})
